import React, { type ReactNode, useContext } from "react";
import type { BaseProps } from "@purpur/common-types";
import { Heading, type HeadingTagType, type HeadingVariantType } from "@purpur/heading";
import { IconCheckmark } from "@purpur/icon/checkmark";
import { Paragraph } from "@purpur/paragraph";
import c from "classnames/bind";

import styles from "./content-block.module.scss";
import { ContentBlockGroup, ContentBlockGroupContext } from "./content-block-group";

const cx = c.bind(styles);
const rootClassName = "purpur-content-block";

export type ContentBlockProps = BaseProps & {
  /**
   * @deprecated Set negative on ContentBlockGroup instead
   */
  negative?: boolean;
  image?: ReactNode;
  video?: ReactNode;
  /**
   * @deprecated Use beforeTitle instead
   */
  badge?: ReactNode;
  actions?: ReactNode;
  listItems?: string[];
  beforeTitle?: ReactNode;
  title?: string;
  headingTag?: HeadingTagType;
  headingVariant?: HeadingVariantType;
};

export const ContentBlock = ({
  className,
  negative,
  image,
  video,
  badge,
  beforeTitle,
  title,
  actions,
  listItems,
  headingTag = "h2",
  headingVariant = "display-15",
  children,
  ...props
}: ContentBlockProps) => {
  const context = useContext(ContentBlockGroupContext);

  const classes = cx([
    rootClassName,
    {
      [`${rootClassName}--negative`]: context?.negative || negative,
      [`${rootClassName}--reverse`]: context?.reverse,
    },
    className,
  ]);

  return (
    <div className={classes} {...props}>
      <div className={cx(`${rootClassName}__section`)}>
        {video ? (
          <div className={cx(`${rootClassName}__video-wrapper`)}>{video}</div>
        ) : (
          <div className={cx(`${rootClassName}__image-wrapper`)}>{image}</div>
        )}
      </div>
      <div className={cx(`${rootClassName}__section`)}>
        <div className={cx(`${rootClassName}__content-wrapper`)}>
          {badge && <div className={cx(`${rootClassName}__badge-wrapper`)}>{badge}</div>}
          {beforeTitle}
          {title?.trim() && (
            <Heading variant={headingVariant} tag={headingTag} negative={negative}>
              {title}
            </Heading>
          )}
          <div className={cx(`${rootClassName}__content`)}>{children}</div>
          {!!listItems?.length && (
            <ul className={cx(`${rootClassName}__list`)}>
              {listItems.map(
                (item) =>
                  item && (
                    <li className={cx(`${rootClassName}__list-item`)} key={item}>
                      <IconCheckmark size="xs" className={cx(`${rootClassName}__list-item-icon`)} />
                      <Paragraph negative={negative}>{item}</Paragraph>
                    </li>
                  )
              )}
            </ul>
          )}
          {actions && <div className={cx(`${rootClassName}__actions`)}>{actions}</div>}
        </div>
      </div>
    </div>
  );
};

ContentBlock.Group = ContentBlockGroup;
ContentBlock.displayName = "ContentBlock";
